/**
 * LeadStageChangeModal — reusable stage change confirmation/notes popup.
 *
 * Mirrors the same mandatory flow as LeadsPage (Mode of Contact + Notes,
 * both required). For "complex" transitions (lead→opportunity,
 * consultation_scheduled, rsvp_office_event, closed_won) that need extra
 * wizards, this modal shows a clear callout and sends the user to the
 * Leads page to complete the change — so AEs never accidentally bypass
 * required data.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Edit, Phone, MessageSquare, Mail, Video, Users, Ban, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTACT_MODES = [
  { value: 'phone_call', label: 'Phone Call', Icon: Phone },
  { value: 'message', label: 'Message', Icon: MessageSquare },
  { value: 'email', label: 'Email', Icon: Mail },
  { value: 'video_call', label: 'Video Call', Icon: Video },
  { value: 'in_person', label: 'In Person', Icon: Users },
  { value: 'no_contact', label: 'No Contact', Icon: Ban },
];

const FALLBACK_LABELS = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  long_term_nurture: 'Long-Term Nurture',
};

// Stages that require wizard flow on LeadsPage
const COMPLEX_STAGES = new Set([
  'consultation_scheduled', // ConsultationWizard
  'rsvp_office_event',       // EventRSVPWizard
  'closed_won',              // Confirmation + contract creation
]);

export default function LeadStageChangeModal({
  open,
  onOpenChange,
  leadId,
  currentStage,
  newStage,              // optional — if not provided, shows an inline stage picker
  stageConfig,           // optional — if provided, used for labels/transitions
  onSuccess,             // optional — callback after successful update
  title,                 // optional override for the dialog title
  skipLabel = 'Cancel',  // optional — label for the left button (e.g. "Skip" in reminder flow)
}) {
  const navigate = useNavigate();
  const [contactMode, setContactMode] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // When newStage is not passed, let the user pick one from inside the modal
  const [pickedStage, setPickedStage] = useState('');
  const effectiveNewStage = newStage || pickedStage;

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setContactMode('');
      setNotes('');
      setSaving(false);
      setPickedStage('');
    }
  }, [open]);

  // Build stage-id → label map
  const stageLabels = useMemo(() => {
    if (stageConfig?.stages?.length) {
      return Object.fromEntries(stageConfig.stages.map(s => [s.stage_id, s.label]));
    }
    return FALLBACK_LABELS;
  }, [stageConfig]);

  // Allowed next stages from current
  const allowedNextStages = useMemo(() => {
    if (!stageConfig?.transitions || !currentStage) return [];
    return stageConfig.transitions[currentStage] || [];
  }, [stageConfig, currentStage]);

  // Detect Lead→Opportunity transitions via stageConfig categories
  const isLeadToOpportunity = useMemo(() => {
    if (!stageConfig?.stages?.length || !currentStage || !effectiveNewStage) return false;
    const from = stageConfig.stages.find(s => s.stage_id === currentStage);
    const to = stageConfig.stages.find(s => s.stage_id === effectiveNewStage);
    return from?.category === 'lead' && to?.category === 'opportunity';
  }, [stageConfig, currentStage, effectiveNewStage]);

  const isComplex = !!effectiveNewStage && (COMPLEX_STAGES.has(effectiveNewStage) || isLeadToOpportunity);
  const showPicker = !newStage; // internal picker mode

  const complexReason = useMemo(() => {
    if (isLeadToOpportunity) return 'Moving a lead to the opportunity pipeline needs deal value, product and cash-collection details.';
    if (effectiveNewStage === 'consultation_scheduled') return 'Scheduling a consultation requires choosing an appointment date & time.';
    if (effectiveNewStage === 'rsvp_office_event') return 'RSVPing to an event requires picking the event.';
    if (effectiveNewStage === 'closed_won') return 'Closing a deal as Won requires creating a contract.';
    return '';
  }, [effectiveNewStage, isLeadToOpportunity]);

  const goToLeads = () => {
    onOpenChange(false);
    navigate(`/leads?stage=${currentStage || 'all'}`);
  };

  const submit = async () => {
    if (!effectiveNewStage) { toast.error('Please choose a new stage'); return; }
    if (!contactMode) { toast.error('Please select a mode of contact'); return; }
    if (!notes.trim()) { toast.error('Please add a note explaining this change'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stage: effectiveNewStage,
          contact_mode: contactMode,
          notes: notes.trim(),
        }),
      });
      if (r.ok) {
        toast.success(`Stage updated to ${stageLabels[effectiveNewStage] || effectiveNewStage}`);
        onOpenChange(false);
        onSuccess?.();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to update stage');
      }
    } catch (err) {
      logger.error('Stage update error', err);
      toast.error('Failed to update stage');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lead-stage-change-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit size={18} className="text-blue-500" />
            {title || 'Update Lead Stage'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {showPicker ? (
            <div>
              <div className="text-sm text-slate-600 mb-2">
                Current stage:{' '}
                <Badge variant="outline">{stageLabels[currentStage] || currentStage || '—'}</Badge>
              </div>
              <Select value={pickedStage} onValueChange={setPickedStage}>
                <SelectTrigger data-testid="reminder-stage-picker">
                  <SelectValue placeholder={allowedNextStages.length ? 'Select new stage…' : 'No transitions available'} />
                </SelectTrigger>
                <SelectContent>
                  {allowedNextStages.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-slate-400">No valid transitions</div>
                  ) : (
                    allowedNextStages.map(sid => (
                      <SelectItem key={sid} value={sid} data-testid={`reminder-stage-opt-${sid}`}>
                        {stageLabels[sid] || sid}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-sm text-slate-600">
              Moving lead from{' '}
              <Badge variant="outline">{stageLabels[currentStage] || currentStage || '—'}</Badge>{' '}
              to{' '}
              <Badge variant="outline">{stageLabels[effectiveNewStage] || effectiveNewStage || '—'}</Badge>
            </div>
          )}

          {!effectiveNewStage ? null : isComplex ? (
            <div className="flex gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="stage-change-complex-warning">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold mb-1">Extra details required</p>
                <p>{complexReason} Please complete this change from the Leads page so you don't miss any required fields.</p>
              </div>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-sm font-medium">
                  Mode of Contact <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2">
                  {CONTACT_MODES.map(mode => {
                    const { Icon } = mode;
                    const selected = contactMode === mode.value;
                    return (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setContactMode(mode.value)}
                        className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${
                          selected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                        data-testid={`task-stage-contact-mode-${mode.value}`}
                      >
                        <Icon size={18} />
                        <span className="text-[11px] mt-1">{mode.label}</span>
                      </button>
                    );
                  })}
                </div>
                {!contactMode && (
                  <p className="text-xs text-red-500 mt-1">Please select a mode of contact</p>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Add a note <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe the reason for this stage change…"
                  className="mt-2 min-h-[90px]"
                  data-testid="task-stage-change-notes"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="stage-change-skip">
            {skipLabel}
          </Button>
          {effectiveNewStage && isComplex ? (
            <Button onClick={goToLeads} data-testid="task-stage-change-goto-leads">
              <ExternalLink size={14} className="mr-1.5" /> Open in Leads
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={saving || !effectiveNewStage || !contactMode || !notes.trim()}
              data-testid="task-stage-change-submit"
            >
              {saving ? 'Updating…' : 'Update Stage'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
